// Settings → Email (#1432): every way enquiries reach a business, side by side, and the address
// replies to each of them leave from. Four cards say where each way stands; the panel below them is
// where it is set up or changed.
//
// Nothing here is decided in the browser. Each card renders a server read. The replies list is the
// server's own answer, computed with the send path's selection. Every panel is an existing screen
// whose routes check the caller again: offering the mailbox editor only to the owner is
// presentation, and `/api/owner/tenants/:id/mailboxes` refuses anyone else regardless.
import React from "react";
import { Button, Card, StatusPill } from "@/ds";
import { cockpit, type MailDomainView, type TenantNeoMailboxView } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { Icon, PanelLabel, SectionNav } from "@/lib";
import { useRoutedState } from "@/lib/routed-state";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import { EMAIL_WAYS, type EmailWay } from "../router";
import { BusinessAddressCard } from "./business-address-card";
import { MailDomainsScreen } from "./screen-mail-domains";
import { TenantMailboxesEditor } from "./screen-tenant-mailboxes";
import { TrustedForwardersCard } from "./trusted-forwarders-card";
import {
  addressCardState,
  domainCardState,
  forwardingCardState,
  heldDomain,
  mailboxCardState,
  ownMailboxAddresses,
  replyRoutes,
  wayAction,
  type SectionRead,
  type WayCardState,
} from "./settings-email.logic";
import styles from "./settings-email.module.css";

export interface SettingsEmailSectionProps {
  /** Which way's panel the URL asks for. Absent means none is open. */
  way?: EmailWay;
  onWayChange?: (way: EmailWay | undefined) => void;
  /**
   * The business an owner is working in, which is what opens the mailbox editor. Null for everyone
   * else: they see the mailbox's state, and the server would refuse them the editor anyway.
   */
  ownerTenantId: string | null;
  pushToast: PushToast;
  onOpenSignature?: () => void;
}

const ICONS: Record<EmailWay, string> = {
  address: "mail",
  forwarding: "inbox",
  domain: "building",
  mailbox: "server",
};

/** How long "Kopiert" stays on the button before it offers to copy again. */
const COPIED_FEEDBACK_MS = 2000;

/** The address on its card: one line and a copy button, without the panel's large presentation. */
function CardAddress({ address }: { address: string }) {
  const t = useT();
  const [copied, setCopied] = React.useState(false);
  // A button that cannot work is absent rather than silent; the address stays selectable text.
  const canCopy = typeof navigator !== "undefined" && Boolean(navigator.clipboard?.writeText);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <span className={styles.address}>
      <span className={styles.addressValue} data-testid="email-way-address">{address}</span>
      {canCopy && (
        <Button
          variant="ghost"
          size="sm"
          className={styles.control}
          iconLeft={<Icon name={copied ? "check" : "clipboard"} size={14} />}
          onClick={() => { void copy(); }}
        >
          {copied ? t("workingAddress.copied") : t("workingAddress.copy")}
        </Button>
      )}
    </span>
  );
}

/** #1536: a remembered answer shows as ready at once; nothing remembered is still loading. */
function rememberedRead<T>(value: T | undefined): SectionRead<T> {
  return value === undefined ? { status: "loading" } : { status: "ready", value };
}

export function SettingsEmailSection({
  way: routedWay,
  onWayChange,
  ownerTenantId,
  pushToast,
  onOpenSignature,
}: SettingsEmailSectionProps) {
  const t = useT();
  const idBase = React.useId();
  const [mailbox, setMailbox] = React.useState<SectionRead<TenantNeoMailboxView>>(
    () => rememberedRead(cachedRead("tenantNeoMailbox")),
  );
  const [forwarders, setForwarders] = React.useState<SectionRead<number>>(
    () => rememberedRead(cachedRead("listTrustedForwarders")?.length),
  );
  const [domains, setDomains] = React.useState<SectionRead<readonly MailDomainView[]>>(
    () => rememberedRead(cachedRead("listMailDomains")),
  );
  // Whether an open panel has reported its own list. The first reads below never overwrite that;
  // a remembered answer they do replace.
  const forwardersReported = React.useRef(false);
  const domainsReported = React.useRef(false);

  // Only the newest read of the mailbox view may paint: a change in a panel asks again, and the two
  // answers can arrive in either order. A later read that fails keeps what an earlier one showed.
  // A view remembered from an earlier visit is not an earlier read: a failed read replaces it.
  const mailboxSequence = React.useRef(0);
  const mailboxAnswered = React.useRef(false);
  const readMailbox = React.useCallback(() => {
    const mine = ++mailboxSequence.current;
    cockpit.tenantNeoMailbox().then(
      (value) => {
        if (mine !== mailboxSequence.current) return;
        mailboxAnswered.current = true;
        setMailbox({ status: "ready", value });
      },
      () => {
        if (mine === mailboxSequence.current) {
          setMailbox((current) => (current.status === "ready" && mailboxAnswered.current ? current : { status: "failed" }));
        }
      },
    );
  }, []);

  React.useEffect(() => {
    let live = true;
    readMailbox();
    // A panel reports its own list once it is open. These first reads fill the cards until then,
    // and never overwrite a panel's newer answer.
    cockpit.listTrustedForwarders().then(
      (rows) => {
        if (live && !forwardersReported.current) setForwarders({ status: "ready", value: rows.length });
      },
      () => {
        if (live && !forwardersReported.current) setForwarders({ status: "failed" });
      },
    );
    cockpit.listMailDomains().then(
      (rows) => {
        if (live && !domainsReported.current) setDomains({ status: "ready", value: rows });
      },
      () => {
        if (live && !domainsReported.current) setDomains({ status: "failed" });
      },
    );
    return () => {
      live = false;
      mailboxSequence.current += 1;
    };
  }, [readMailbox]);

  const onForwarderCount = React.useCallback((count: number) => {
    forwardersReported.current = true;
    setForwarders({ status: "ready", value: count });
  }, []);
  // A domain that starts or stops sending changes where replies leave from, so the server is asked
  // again rather than the list being patched here.
  const onDomainsChange = React.useCallback((rows: readonly MailDomainView[]) => {
    domainsReported.current = true;
    setDomains({ status: "ready", value: rows });
    readMailbox();
  }, [readMailbox]);
  const onMailboxesChange = React.useCallback(() => {
    readMailbox();
  }, [readMailbox]);

  const [way, setWay] = useRoutedState<EmailWay | undefined>(routedWay, onWayChange, undefined);
  // A panel stays mounted once opened and is only hidden while another is chosen, so a half-typed
  // address or domain survives a look at a different card (the Account screen's rule).
  const [visited, setVisited] = React.useState<ReadonlySet<EmailWay>>(() => new Set(way ? [way] : []));
  React.useEffect(() => {
    if (way) setVisited((current) => (current.has(way) ? current : new Set([...current, way])));
  }, [way]);

  const choose = (next: EmailWay | undefined) => setWay(next);

  const canEditMailbox = ownerTenantId !== null;
  const domain = heldDomain(domains);
  const ownMailboxes = ownMailboxAddresses(mailbox);
  const address = mailbox.status === "ready" ? mailbox.value.emailAddress : null;
  // Only an address on our own mail server is "ours"; one the business brought is its working address.
  const hosted = mailbox.status === "ready" ? mailbox.value.hosted : true;
  const routes = replyRoutes(mailbox);

  const states: Record<EmailWay, WayCardState> = {
    address: addressCardState(mailbox),
    forwarding: forwardingCardState(forwarders),
    domain: domainCardState(domains),
    mailbox: mailboxCardState(mailbox),
  };
  const titles: Record<EmailWay, string> = {
    address: hosted ? t("businessAddress.title") : t("settingsEmail.address.titleOwn"),
    forwarding: t("settingsEmail.forwarding.title"),
    domain: t("settingsEmail.domain.title"),
    mailbox: t("settingsEmail.mailbox.title"),
  };
  const waiting = (state: WayCardState): string | null => {
    if (state.state === "loading") return t("common.loading");
    if (state.state === "unavailable") return t("settingsEmail.unavailableDetail");
    return null;
  };
  const details: Record<EmailWay, React.ReactNode> = {
    address: waiting(states.address)
      ?? (address ? <CardAddress address={address} /> : t("settingsEmail.address.missingDetail")),
    forwarding: t("settingsEmail.forwarding.detail"),
    domain: `${domain ? domain.emailAddress : t("settingsEmail.domain.detailNone")} · ${t("settingsEmail.domain.onePer")}`,
    mailbox: waiting(states.mailbox)
      ?? (ownMailboxes.length > 0
        ? ownMailboxes.join(", ")
        : t(canEditMailbox ? "settingsEmail.mailbox.detailNone" : "settingsEmail.mailbox.byOwner")),
  };

  const titleId = (id: EmailWay) => `${idBase}-${id}-title`;
  const actionId = (id: EmailWay) => `${idBase}-${id}-action`;
  const panelId = (id: EmailWay) => `${idBase}-${id}-panel`;
  const contentId = `${idBase}-content`;
  const mounted = (id: EmailWay) => id === way || visited.has(id);

  const panel = (id: EmailWay): React.ReactNode => {
    switch (id) {
      case "address":
        // A panel that opens empty explains nothing, and the card below would render exactly that
        // after a failed read: it answers a failure with nothing at all, by design.
        if (mailbox.status === "failed") {
          return (
            <Card data-testid="email-address-unavailable">
              <PanelLabel icon="alert">{titles.address}</PanelLabel>
              <p className="management-copy">{t("settingsEmail.unavailableDetail")}</p>
            </Card>
          );
        }
        // The card Settings → System used to show, with its instructions (#779, #1229).
        return address || mailbox.status === "loading"
          ? <BusinessAddressCard />
          : (
            <Card data-testid="email-address-missing">
              <PanelLabel icon="alert">{titles.address}</PanelLabel>
              <p className="management-copy">{t("settingsEmail.address.missingDetail")}</p>
            </Card>
          );
      case "forwarding":
        return <TrustedForwardersCard pushToast={pushToast} onCount={onForwarderCount} />;
      case "domain":
        return <MailDomainsScreen pushToast={pushToast} onChange={onDomainsChange} />;
      case "mailbox":
        return ownerTenantId
          ? (
            <Card data-testid="email-mailbox-editor">
              <PanelLabel icon="server">{titles.mailbox}</PanelLabel>
              <TenantMailboxesEditor tenantId={ownerTenantId} pushToast={pushToast} onRowsChange={onMailboxesChange} />
            </Card>
          )
          : (
            <Card data-testid="email-mailbox-status">
              <PanelLabel icon="server">{titles.mailbox}</PanelLabel>
              {ownMailboxes.length > 0 && (
                <ul className={styles.plainList}>
                  {ownMailboxes.map((own) => <li key={own} className={styles.routeAddress}>{own}</li>)}
                </ul>
              )}
              <p className="management-copy">{t("settingsEmail.mailbox.adminNote")}</p>
            </Card>
          );
    }
  };

  return (
    <div className={styles.section} data-testid="settings-email">
      <SectionNav active={way ?? "overview"} panelId={contentId} ariaLabel={t("settings.section.email")}
        entries={[
          { id: "overview", label: t("settingsEmail.overview") },
          ...EMAIL_WAYS.map((id) => ({ id, label: t(`settingsEmail.tab.${id}`) })),
        ]} onSelect={(id) => choose(id === "overview" ? undefined : id as EmailWay)} />
      <div id={contentId} role="tabpanel" aria-labelledby={`${contentId}-tab-${way ?? "overview"}`} tabIndex={0}>
      <div hidden={Boolean(way)}>
      <p className={`management-copy ${styles.lead}`}>{t("settingsEmail.lead")}</p>

      <ul className={styles.grid} aria-label={t("settingsEmail.waysAria")}>
        {EMAIL_WAYS.map((id) => {
          const card = states[id];
          const selected = way === id;
          return (
            <li key={id} className={styles.cell}>
              <Card
                className={selected ? `${styles.card} ${styles.selected}` : styles.card}
                data-testid="email-way"
                data-way={id}
                data-state={card.state}
                data-selected={selected}
              >
                <div className={styles.cardHead}>
                  <span className={styles.icon} aria-hidden="true"><Icon name={ICONS[id]} size={18} /></span>
                  <h3 id={titleId(id)} className={styles.title}>{titles[id]}</h3>
                  {card.pill && (
                    <StatusPill size="sm" tone={card.pill.tone} label={t(card.pill.key, card.pill.vars)} />
                  )}
                </div>
                <div className={styles.detail}>{details[id]}</div>
                <Button
                  id={actionId(id)}
                  variant="secondary"
                  size="sm"
                  className={`${styles.control} ${styles.action}`}
                  aria-pressed={selected}
                  aria-controls={mounted(id) ? panelId(id) : undefined}
                  aria-labelledby={`${actionId(id)} ${titleId(id)}`}
                  onClick={() => {
                    choose(id);
                    window.requestAnimationFrame(() => document.getElementById(`${contentId}-tab-${id}`)?.focus());
                  }}
                  data-testid={`email-way-open-${id}`}
                >
                  {t(`settingsEmail.open.${wayAction(id, card, canEditMailbox)}`)}
                </Button>
              </Card>
            </li>
          );
        })}
      </ul>

      <Card className={styles.replies} data-testid="email-replies">
        <h3 className={styles.title}>{t("settingsEmail.replies.title")}</h3>
        <p className={styles.detail}>{t("settingsEmail.replies.lead")}</p>
        {routes === null
          ? <p className={styles.detail}>{mailbox.status === "loading" ? t("common.loading") : t("settingsEmail.unavailableDetail")}</p>
          : routes.length === 0
            ? <p className={styles.detail} data-testid="email-replies-empty">{t("settingsEmail.replies.empty")}</p>
            : (
              <ul className={styles.routes}>
                {routes.map((route) => (
                  <li
                    key={route.address}
                    className={styles.route}
                    data-testid="email-reply-route"
                    data-kind={route.kind}
                    data-replies-from={route.repliesFrom ?? ""}
                  >
                    <span className={styles.routeFrom}>
                      <span className={styles.kind}>{t(`settingsEmail.kind.${route.kind}`)}</span>
                      <span className={styles.routeAddress}>{route.address}</span>
                    </span>
                    <Icon name="arrowRight" size={16} className={styles.arrow} />
                    <span className={styles.routeTo}>
                      <span className="sr-only">{t("settingsEmail.replies.answeredFrom")} </span>
                      {route.repliesFrom
                        ? <span className={styles.routeAddress}>{route.repliesFrom}</span>
                        : <span className={styles.cannot}>{t("settingsEmail.replies.cannot")}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
        {onOpenSignature && (
          <div className={styles.repliesFoot}>
            <span className={styles.detail}>{t("settingsEmail.replies.signature")}</span>
            <Button
              variant="ghost"
              size="sm"
              className={styles.control}
              iconRight={<Icon name="arrowRight" size={14} />}
              onClick={onOpenSignature}
              data-testid="email-replies-signature"
            >
              {t("settingsEmail.replies.openSignature")}
            </Button>
          </div>
        )}
      </Card>

      </div>
      {EMAIL_WAYS.filter(mounted).map((id) => (
        <div
          key={id}
          id={panelId(id)}
          role="region"
          aria-labelledby={`${contentId}-tab-${id}`}
          hidden={id !== way}
          className={styles.panel}
          data-testid={`email-panel-${id}`}
        >
          {panel(id)}
        </div>
      ))}
      </div>
    </div>
  );
}
